import QRCode from 'qrcode';
import { Alpine } from '../core/alpine-core.js';
import { preferredAppOrigin } from '../core/preferred-app-origin.js';
import { apiFetch } from './api.js';

function refreshKioskNavEntitlement() {
  try {
    Alpine.store('kioskEntitlement')?.refresh?.();
  } catch {
    // store may be unavailable outside admin shell
  }
}

function applyEffectiveFromAccountResponse(data) {
  try {
    Alpine.store('features')?.applyServerCapabilities?.(data?.effectiveCapabilities);
    Alpine.store('subscriptionGate')?.setFromAccountSettings?.(data);
  } catch {
    /* ignore */
  }
}

function formatMoney(amount, currency = 'GBP') {
  const safe = Number(amount);
  if (!Number.isFinite(safe)) return '—';
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: String(currency || 'GBP'),
      maximumFractionDigits: 0,
    }).format(safe);
  } catch {
    return `${safe}`;
  }
}

function emptyControlPlane() {
  return {
    configured: false,
    plans: [],
    featureAddons: [],
    entitlement: null,
    billing: {},
    featureLabels: {},
  };
}

function normalizeControlPlane(raw) {
  const cp = raw && typeof raw === 'object' ? raw : {};
  return {
    ...emptyControlPlane(),
    ...cp,
    plans: Array.isArray(cp.plans) ? cp.plans : [],
    featureAddons: Array.isArray(cp.featureAddons) ? cp.featureAddons : [],
    billing: cp.billing && typeof cp.billing === 'object' ? cp.billing : {},
    featureLabels: cp.featureLabels && typeof cp.featureLabels === 'object' ? cp.featureLabels : {},
  };
}

function featureToPillLabel(feature) {
  const raw = String(feature || '').trim();
  if (!raw) return '';
  const pretty = raw
    .replaceAll(/[_-]+/g, ' ')
    .replaceAll(/\s+/g, ' ')
    .trim();
  return pretty
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

Alpine.data('accountSettingsPage', () => ({
  formatMoney,
  loading: true,
  saving: false,
  savingMapsKey: false,
  clearingMapsKey: false,
  savingKey: false,
  clearingKey: false,
  refreshing: false,
  billingCheckoutLoading: false,
  billingPortalLoading: false,
  billingContactEmail: '',
  planGateActive: false,
  planGateNeedCamel: '',
  /** ISO timestamp — last successful load of account settings / control-plane snapshot for this page */
  entitlementFetchedAt: null,
  error: '',
  accountId: '',
  settings: null,
  controlPlane: emptyControlPlane(),
  form: {
    controlPlaneBaseUrl: '',
    subscriptionKey: '',
    googleMapsApiKey: '',
  },
  mailForm: {
    mode: 'platform',
    smtpHost: '',
    smtpPort: 587,
    smtpSecure: false,
    smtpUser: '',
    smtpPass: '',
    fromOverride: '',
    testTo: '',
  },
  savingMail: false,
  mailTestLoading: false,
  participateToggleLoading: false,
  personClaimCode: '',
  personClaimLoading: false,
  activeTab: 'layout',
  kioskLaunchUrlValue: '',
  kioskQrDataUrl: '',
  kioskQrError: '',

  accountTabs: [
    { id: 'profile', label: 'My profile', hint: 'Sign-in & player' },
    { id: 'layout', label: 'Layout', hint: 'Home & dashboard' },
    { id: 'backup', label: 'Backup', hint: 'Mongo mirror' },
    { id: 'subscription', label: 'Subscription', hint: 'Keys & entitlement' },
    { id: 'plans', label: 'Plans', hint: 'Tiers & billing' },
    { id: 'people', label: 'Team', hint: 'Invites & roles' },
    { id: 'email', label: 'Email', hint: 'SMTP setup' },
  ],

  peopleItems: [],
  peopleLoading: false,
  peopleError: '',
  inviteForm: { flow: 'staff', email: '', role: 'manager', playerId: '' },
  inviteRosterPlayers: [],
  inviting: false,
  roleSaveEmail: null,
  removeBusyEmail: null,

  /** Matches server `roleRank` (manager < admin < owner). Not `session.hasRole('admin')`, which treats managers as admins. */
  venueStaffRoleRank(role) {
    const rk = { player: 1, viewer: 1, manager: 2, admin: 3, owner: 4 };
    return rk[String(role || '').toLowerCase()] || 0;
  },

  hasAtLeastVenueRole(minRole) {
    const need = this.venueStaffRoleRank(minRole);
    const got = this.venueStaffRoleRank(this.$store?.session?.me?.role);
    return need > 0 && got >= need;
  },

  isVenueOwner() {
    return String(this.$store?.session?.me?.role || '').toLowerCase() === 'owner';
  },

  /** Admin or owner only — maps to `requireAdminRole("admin")` on the API. */
  canManageAccountIntegrations() {
    return this.hasAtLeastVenueRole('admin');
  },

  /** Stripe checkout & customer portal — `requireAdminRole("admin")`. */
  canManageBillingSelfServe() {
    return this.hasAtLeastVenueRole('admin');
  },

  visibleAccountTabs() {
    const r = String(this.$store?.session?.me?.role || '').toLowerCase();
    const showPeople = ['owner', 'admin', 'manager'].includes(r);
    const showEmail = r === 'owner';
    const slimPlayer = this.$store?.session?.isPlayerPortalSlim === true;
    return this.accountTabs.filter(
      (t) =>
        (t.id !== 'people' || showPeople) &&
        (t.id !== 'email' || showEmail) &&
        (t.id !== 'profile' || true),
    );
  },

  peopleRoleRank() {
    return { player: 1, viewer: 1, manager: 2, admin: 3, owner: 4 };
  },

  peopleInviterRank() {
    const r = String(this.$store?.session?.me?.role || '').toLowerCase();
    return this.peopleRoleRank()[r] || 0;
  },

  canInviteStaff() {
    const r = String(this.$store?.session?.me?.role || '').toLowerCase();
    return r === 'owner' || r === 'admin';
  },

  canEditStaffRoles() {
    return this.canInviteStaff();
  },

  /** Staff-only roles for the “Invite staff” form (player portal uses the separate flow). */
  inviteStaffRoleOptions() {
    const max = this.peopleInviterRank();
    const rk = this.peopleRoleRank();
    const order = ['viewer', 'manager', 'admin', 'owner'];
    const labels = {
      owner: 'Owner (org)',
      admin: 'Admin',
      manager: 'Manager',
      viewer: 'Viewer',
    };
    return order
      .filter((id) => (rk[id] || 0) <= max)
      .map((id) => ({ id, label: labels[id] || id }));
  },

  /** Role dropdown on the people table — cannot assign “player” except when already a player login. */
  staffRoleChangeOptions(row) {
    const max = this.peopleInviterRank();
    const rk = this.peopleRoleRank();
    const current = String(row?.role || 'viewer').toLowerCase();
    const isPlayer = current === 'player';
    const order = isPlayer ? ['player', 'viewer', 'manager', 'admin', 'owner'] : ['viewer', 'manager', 'admin', 'owner'];
    const labels = {
      owner: 'Owner (org)',
      admin: 'Admin',
      manager: 'Manager',
      viewer: 'Viewer',
      player: 'Player (portal)',
    };
    const allowed = order.filter((id) => (rk[id] || 0) <= max);
    if (current && !allowed.includes(current)) {
      allowed.unshift(current);
    }
    return allowed.map((id) => ({ id, label: labels[id] || id }));
  },

  selfPeopleEmail() {
    return String(this.$store?.session?.me?.email || '').trim().toLowerCase();
  },

  isSelfPeopleRow(row) {
    return String(row?.email || '').trim().toLowerCase() === this.selfPeopleEmail();
  },

  canManagePeopleRow(row) {
    if (!this.canEditStaffRoles() || this.isSelfPeopleRow(row)) return false;
    const max = this.peopleInviterRank();
    const rk = this.peopleRoleRank();
    const targetRank = rk[String(row?.role || '').toLowerCase()] || 0;
    return targetRank <= max;
  },

  roleDisplayLabel(role) {
    const labels = {
      owner: 'Owner (org)',
      admin: 'Admin',
      manager: 'Manager',
      viewer: 'Viewer',
      player: 'Player (portal)',
    };
    return labels[String(role || '').toLowerCase()] || String(role || '—');
  },

  normalizePeopleRow(raw) {
    const email = String(raw?.email || '').trim().toLowerCase();
    const role = String(raw?.role || 'viewer').trim().toLowerCase();
    return {
      ...raw,
      email,
      role: ['owner', 'admin', 'manager', 'viewer', 'player'].includes(role) ? role : 'viewer',
      playerId: raw?.playerId ?? null,
      pendingSetup: !!raw?.pendingSetup,
    };
  },

  get saveControlPlaneUrlAllowed() {
    return this.controlPlane?.saveControlPlaneUrlAllowed === true;
  },

  /** Admin static root (e.g. `/admin/`) — same idea as `__BASE_URL__ + 'partials/…'` in the shell. */
  adminViewsRoot() {
    return `${String(globalThis.__BASE_URL__ || '/admin/').replace(/\/?$/, '')}/`;
  },

  embedLayoutUrl() {
    return `${this.adminViewsRoot()}views/embed-dashboard-layout.html`;
  },

  embedBackupUrl() {
    return `${this.adminViewsRoot()}views/embed-backup-sync.html`;
  },

  resolveKioskLaunchUrl() {
    const origin = String(preferredAppOrigin() || '').trim();
    if (!origin) return '/venue-kiosk';
    return `${origin}/venue-kiosk`;
  },

  kioskAccessEnabled() {
    return this.currentEntitlement()?.kioskAllowed === true;
  },

  async refreshKioskAccessCard() {
    this.kioskLaunchUrlValue = this.resolveKioskLaunchUrl();
    this.kioskQrError = '';
    if (!this.kioskAccessEnabled()) {
      this.kioskQrDataUrl = '';
      return;
    }
    try {
      this.kioskQrDataUrl = await QRCode.toDataURL(this.kioskLaunchUrlValue, {
        width: 220,
        margin: 1,
        color: { dark: '#0f172a', light: '#ffffff' },
      });
    } catch {
      this.kioskQrDataUrl = '';
      this.kioskQrError = 'Could not generate QR code';
    }
  },

  async copyKioskLaunchUrl() {
    const url = String(this.kioskLaunchUrlValue || '').trim();
    if (!url) return;
    try {
      await globalThis.navigator?.clipboard?.writeText(url);
      this.$store.toastr.success('Kiosk URL copied');
    } catch {
      this.$store.toastr.error('Could not copy URL');
    }
  },

  async copyAccountId() {
    const id = String(this.accountId || '').trim();
    if (!id) return;
    try {
      await globalThis.navigator?.clipboard?.writeText(id);
      this.$store.toastr.success('Account ID copied');
    } catch {
      this.$store.toastr.error('Could not copy account ID');
    }
  },

  syncTabFromHash() {
    let raw = (globalThis.location?.hash || '').replace(/^#/, '').trim();
    const base = globalThis.location?.pathname + (globalThis.location?.search || '');
    if (raw === 'upgrade') {
      raw = 'plans';
      globalThis.history.replaceState(null, '', `${base}#plans`);
    }
    if (raw === 'people') {
      const r = String(this.$store?.session?.me?.role || '').toLowerCase();
      if (!['owner', 'admin', 'manager'].includes(r)) {
        raw = 'layout';
        globalThis.history.replaceState(null, '', `${base}#layout`);
      }
    }
    if (raw === 'email' && !this.isVenueOwner()) {
      raw = 'layout';
      globalThis.history.replaceState(null, '', `${base}#layout`);
    }
    if (raw === 'profile' && this.$store?.session?.isPlayerPortalSlim === true) {
      raw = 'layout';
      globalThis.history.replaceState(null, '', `${base}#layout`);
    }
    const allowed = new Set(['profile', 'layout', 'backup', 'subscription', 'plans', 'people', 'email']);
    if (allowed.has(raw)) this.activeTab = raw;
  },

  setTab(id) {
    this.activeTab = id;
    const base = globalThis.location?.pathname + (globalThis.location?.search || '');
    globalThis.history.replaceState(null, '', `${base}#${id}`);
    if (id === 'people') this.loadPeople();
  },

  focusPlansInSubscription() {
    this.setTab('plans');
  },

  async init() {
    this.syncTabFromHash();
    globalThis.addEventListener('hashchange', () => {
      this.syncTabFromHash();
      if (this.activeTab === 'people') this.loadPeople();
    });
    await this.reload();
    this.readPlanGateFromUrl();
    // Hash may be applied after SPA navigate (router strips # for matching, then restores on pinecone:end).
    this.syncTabFromHash();
    queueMicrotask(() => {
      this.syncTabFromHash();
      if (this.activeTab === 'people') this.loadPeople();
    });
  },

  readPlanGateFromUrl() {
    try {
      const u = new URL(globalThis.location.href);
      if (u.searchParams.get('gate') !== '1') return;
      const need = String(u.searchParams.get('need') || '').trim();
      const allowed = new Set(['challenges', 'training', 'backupSync', 'league', 'liveFixtures', 'playerPortal', 'kiosk']);
      if (need && allowed.has(need)) {
        this.planGateNeedCamel = need;
        this.planGateActive = true;
        if (!u.hash || u.hash === '#') this.activeTab = 'plans';
      }
      u.searchParams.delete('gate');
      u.searchParams.delete('need');
      const qs = u.searchParams.toString();
      let h = u.hash || '';
      if (this.planGateActive && (!h || h === '#')) h = '#plans';
      globalThis.history.replaceState(null, '', u.pathname + (qs ? `?${qs}` : '') + h);
    } catch {
      /* ignore */
    }
  },

  planGateNeedSnake() {
    const m = {
      challenges: 'challenges',
      training: 'training',
      backupSync: 'backup_sync',
      league: 'league',
      liveFixtures: 'live_fixtures',
      playerPortal: 'player_portal',
      kiosk: 'kiosk',
    };
    return m[this.planGateNeedCamel] || '';
  },

  planGateFeatureLabel() {
    const snake = this.planGateNeedSnake();
    const labels = this.controlPlane?.featureLabels || {};
    if (snake && labels[snake]) return labels[snake];
    if (this.planGateNeedCamel) return featureToPillLabel(this.planGateNeedCamel.replace(/([A-Z])/g, ' $1').trim());
    return 'This feature';
  },

  scrollToPlansTiers() {
    globalThis.document.getElementById('account-settings-plans')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  },

  scrollToAddonsSection() {
    globalThis.document.getElementById('account-settings-addons')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  },

  requestAddonMailtoHref() {
    const snake = this.planGateNeedSnake();
    const to = String(this.billingContactEmail || '').trim();
    if (!to || !snake) return '';
    const subj = encodeURIComponent(`DartsOS add-on request: ${snake}`);
    const body = encodeURIComponent(
      `Please enable feature "${snake}" for our account.\n\nAccount / context (fill in):\n\n`,
    );
    return `mailto:${to}?subject=${subj}&body=${body}`;
  },

  currentEntitlement() {
    return this.controlPlane?.entitlement || null;
  },

  /** Control plane reports this account has no subscription — hide plan/privilege/feature pills. */
  noActiveSubscription() {
    const cp = this.controlPlane;
    if (!cp?.configured) return false;
    return this.currentEntitlement()?.noSubscription === true;
  },

  backupSyncEntitled() {
    const cp = this.controlPlane;
    if (!cp?.configured) return true;
    const e = cp.entitlement;
    if (!e || cp.error) return false;
    if (e.noSubscription === true) return false;
    if (e.backupSyncAllowed === true) return true;
    const feats = Array.isArray(e.features) ? e.features : [];
    return feats.map((f) => String(f).toLowerCase()).includes('backup_sync');
  },

  currentPlan() {
    const tier = String(this.currentEntitlement()?.tier || '').trim();
    const plans = Array.isArray(this.controlPlane?.plans) ? this.controlPlane.plans : [];
    return plans.find((p) => String(p?.id || '') === tier) || null;
  },

  currentPlanLabel() {
    const plan = this.currentPlan();
    if (plan?.label) return String(plan.label);
    const tier = String(this.currentEntitlement()?.tier || '').trim();
    if (!tier) return '—';
    return 'Custom';
  },

  entitlementFeaturePills() {
    const features = Array.isArray(this.currentEntitlement()?.features)
      ? this.currentEntitlement().features
      : [];
    return features
      .map(featureToPillLabel)
      .filter(Boolean);
  },

  planCompareHint(plan) {
    const cur = this.currentPlan();
    const entTier = String(this.currentEntitlement()?.tier || '').trim();
    if (entTier && !cur) {
      return 'Your active tier is not in the catalog below (custom or legacy key). Published plans are for comparison only.';
    }
    if (!cur || String(cur?.id) === String(plan?.id)) return 'This matches your current tier.';
    const curIdx = (this.controlPlane?.plans || []).findIndex((p) => String(p?.id) === String(cur?.id));
    const idx = (this.controlPlane?.plans || []).findIndex((p) => String(p?.id) === String(plan?.id));
    if (curIdx < 0 || idx < 0) return 'Compare features and kiosk device limits before switching.';
    if (idx < curIdx) return 'Lower tier: fewer devices or features — confirm in the control plane when billing is available.';
    if (idx > curIdx) return 'Higher tier: more capacity — use Upgrade to open Stripe Checkout when billing is enabled.';
    return '';
  },

  paidStripePlanIds() {
    return new Set(['bullseye_solo', 'double_top_duo', 'ton80_five']);
  },

  planCheckoutEnabled(plan) {
    return this.controlPlane?.billing?.checkoutAvailable === true && this.paidStripePlanIds().has(String(plan?.id || ''));
  },

  planUpgradeRank(planOrTier) {
    const id = planOrTier && typeof planOrTier === 'object' ? String(planOrTier?.id || '') : String(planOrTier || '');
    const plans = Array.isArray(this.controlPlane?.plans) ? this.controlPlane.plans : [];
    return plans.findIndex((p) => String(p?.id) === id);
  },

  currentPlanRank() {
    const cur = this.currentPlan();
    let idx = this.planUpgradeRank(cur);
    if (idx < 0) {
      const tier = String(this.currentEntitlement()?.tier || '').trim();
      if (tier) idx = this.planUpgradeRank(tier);
    }
    return idx;
  },

  canStripeUpgrade(plan) {
    if (!this.planCheckoutEnabled(plan)) return false;
    if (String(this.currentPlan()?.id || '') === String(plan?.id || '')) return false;
    const curIdx = this.currentPlanRank();
    const idx = this.planUpgradeRank(plan);
    if (idx < 0) return false;
    if (curIdx < 0) return true;
    return idx > curIdx;
  },

  planActionLabel(plan) {
    const id = String(plan?.id || '');
    if (id === 'open_oche') return 'Free tier';
    const cur = this.currentPlan();
    if (cur && String(cur?.id) === String(plan?.id)) return 'Current plan';
    const curIdx = (this.controlPlane?.plans || []).findIndex((p) => String(p?.id) === String(cur?.id));
    const idx = (this.controlPlane?.plans || []).findIndex((p) => String(p?.id) === String(plan?.id));
    if (curIdx >= 0 && idx >= 0) {
      if (idx < curIdx) return 'Downgrade (use billing portal)';
      if (idx > curIdx) return this.planCheckoutEnabled(plan) ? 'Upgrade via Stripe' : 'Upgrade (configure billing)';
    }
    if (!this.paidStripePlanIds().has(id)) return '—';
    return this.planCheckoutEnabled(plan) ? 'Subscribe via Stripe' : 'Billing not configured';
  },

  billingReturnUrls() {
    const origin = String(globalThis.location?.origin || '').trim();
    const path = String(globalThis.location?.pathname || '/admin/account-settings').trim() || '/admin/account-settings';
    const base = origin ? `${origin}${path}` : '';
    return {
      successUrl: base ? `${base}#subscription` : '',
      cancelUrl: base ? `${base}#plans` : '',
      portalReturnUrl: base ? `${base}#subscription` : '',
    };
  },

  async startStripeCheckout(plan, billingPeriod) {
    if (this.billingCheckoutLoading) return;
    if (!this.canManageBillingSelfServe()) return;
    if (!this.canStripeUpgrade(plan)) return;
    const { successUrl, cancelUrl } = this.billingReturnUrls();
    if (!/^https?:\/\//i.test(successUrl) || !/^https?:\/\//i.test(cancelUrl)) {
      this.$store.toastr.error('Need a browser origin to build checkout URLs');
      return;
    }
    this.billingCheckoutLoading = true;
    try {
      const res = await apiFetch('/api/admin/account-settings/billing/checkout', {
        method: 'POST',
        body: {
          targetTier: String(plan?.id || ''),
          billingPeriod,
          successUrl,
          cancelUrl,
        },
        timeoutMs: 25000,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Checkout failed');
      const url = String(data?.url || '').trim();
      if (!url) throw new Error('No checkout URL returned');
      globalThis.location.assign(url);
    } catch (e) {
      this.$store.toastr.error(e?.message || 'Checkout failed');
    } finally {
      this.billingCheckoutLoading = false;
    }
  },

  async openStripeCustomerPortal() {
    if (this.billingPortalLoading || !this.canManageBillingSelfServe()) return;
    if (this.controlPlane?.billing?.customerPortalAvailable !== true) return;
    const { portalReturnUrl } = this.billingReturnUrls();
    if (!/^https?:\/\//i.test(portalReturnUrl)) {
      this.$store.toastr.error('Need a browser origin to return from the billing portal');
      return;
    }
    this.billingPortalLoading = true;
    try {
      const res = await apiFetch('/api/admin/account-settings/billing/portal', {
        method: 'POST',
        body: { returnUrl: portalReturnUrl },
        timeoutMs: 25000,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Portal session failed');
      const url = String(data?.url || '').trim();
      if (!url) throw new Error('No portal URL returned');
      globalThis.location.assign(url);
    } catch (e) {
      this.$store.toastr.error(e?.message || 'Portal failed');
    } finally {
      this.billingPortalLoading = false;
    }
  },

  deltaLabel(plan, term) {
    const cur = this.currentPlan();
    if (!cur) return '';
    const field = term === 'yearly' ? 'yearlyPrice' : 'monthlyPrice';
    const diff = Number(plan?.[field] || 0) - Number(cur?.[field] || 0);
    if (!Number.isFinite(diff) || diff === 0) return 'same as current';
    const sign = diff > 0 ? '+' : '-';
    return `${sign}${formatMoney(Math.abs(diff), plan?.currency || 'GBP')}`;
  },

  entitlementTone() {
    const ent = this.currentEntitlement();
    if (!ent) return 'neutral';
    if (ent.kioskAllowed) return 'ok';
    if (ent.noSubscription || ent.upgradeRequired) return 'warn';
    return 'neutral';
  },

  entitlementMessage() {
    const ent = this.currentEntitlement();
    if (!this.controlPlane?.configured) {
      return 'Control-plane entitlement checks are not configured on this server yet.';
    }
    if (this.controlPlane?.error) return this.controlPlane.error;
    if (!ent) return 'No entitlement response available.';
    if (ent.trialExpired === true) {
      return 'Your trial has ended. Open Plans to compare paid tiers, then upgrade via the control plane (Stripe checkout when enabled).';
    }
    if (ent.isTrial === true && typeof ent.daysRemaining === 'number' && ent.daysRemaining >= 0) {
      return `Trial active — ${ent.daysRemaining} day${ent.daysRemaining === 1 ? '' : 's'} remaining on account ${ent.accountId || ''}.`;
    }
    if (ent.kioskAllowed) return `Kiosk feature is active for account ${ent.accountId || ''}.`;
    if (ent.noSubscription) return 'No active subscription found for this account.';
    if (ent.upgradeRequired) return 'Current plan does not include kiosk entitlement.';
    return 'Subscription is present but kiosk entitlement is unavailable.';
  },

  maxLeaguesLabel() {
    const eff = this.$store?.features?.subscription;
    const n = eff?.maxLeagues;
    if (n === null || n === undefined) return 'Unlimited';
    return String(n);
  },

  planPrice(plan, term) {
    const field = term === 'yearly' ? 'yearlyPrice' : 'monthlyPrice';
    return formatMoney(Number(plan?.[field] || 0), plan?.currency || 'GBP');
  },

  touchEntitlementFetchedAt() {
    try {
      this.entitlementFetchedAt = new Date().toISOString();
    } catch {
      /* ignore */
    }
  },

  entitlementFetchedAtLabel() {
    const raw = this.entitlementFetchedAt;
    if (!raw) return '';
    try {
      return new Date(raw).toLocaleString();
    } catch {
      return '';
    }
  },

  /** Link or unlink a roster player row for staff (owner / admin / manager / viewer). */
  async toggleParticipateAsPlayer(ev) {
    const el = ev?.target;
    const wantOn = !!el?.checked;
    const prev = !!this.$store?.session?.rosterSelfPlayerId;
    this.participateToggleLoading = true;
    try {
      const res = await apiFetch('/api/admin/auth/me', {
        method: 'PATCH',
        body: { participateAsPlayer: wantOn },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (el) el.checked = prev;
        this.$store.toastr.error(String(data.error || data.message || 'Could not update'));
        return;
      }
      const S = this.$store?.session;
      if (S?.refreshMe) await S.refreshMe();
      if (el) el.checked = !!this.$store?.session?.rosterSelfPlayerId;
      this.$store.toastr.success(
        wantOn
          ? 'League player profile created — assign yourself to a team to enter fixtures.'
          : 'League player link removed from this sign-in (existing roster data is kept).',
      );
    } catch (e) {
      if (el) el.checked = prev;
      this.$store.toastr.error(String(e?.message || 'Request failed'));
    } finally {
      this.participateToggleLoading = false;
    }
  },

  async submitPersonClaim() {
    const code = String(this.personClaimCode || '').trim();
    if (!code) {
      this.$store.toastr.warning('Enter a claim code');
      return;
    }
    this.personClaimLoading = true;
    try {
      const res = await apiFetch('/auth/person-claim', { method: 'POST', body: { claimCode: code } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || 'Claim failed');
      this.personClaimCode = '';
      await this.$store?.session?.refreshMe?.(true);
      this.$store.toastr.success('Roster player linked to your account');
    } catch (e) {
      this.$store.toastr.error(e?.message || 'Claim failed');
    } finally {
      this.personClaimLoading = false;
    }
  },

  async reload({ withSpinner = true } = {}) {
    if (withSpinner) this.loading = true;
    this.error = '';
    try {
      const res = await apiFetch(`/api/admin/account-settings?_ts=${Date.now()}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load account settings');
      this.billingContactEmail = String(data.billingContactEmail || '').trim();
      this.accountId = String(data.accountId || '').trim();
      this.settings = data.settings || null;
      this.controlPlane = normalizeControlPlane(data.controlPlane);
      this.form.controlPlaneBaseUrl = String(this.settings?.controlPlaneBaseUrl || '');
      this.form.subscriptionKey = '';
      this.form.googleMapsApiKey = '';
      const md = data.mailDelivery || {};
      this.mailForm.mode = md.mode || 'platform';
      this.mailForm.smtpHost = md.smtpHost || '';
      this.mailForm.smtpPort = Number(md.smtpPort) || 587;
      this.mailForm.smtpSecure = !!md.smtpSecure;
      this.mailForm.smtpUser = md.smtpUser || '';
      this.mailForm.smtpPass = '';
      this.mailForm.fromOverride = md.fromOverride || '';
      this.mailForm.testTo = String(this.$store?.session?.me?.email || '').trim();
      applyEffectiveFromAccountResponse(data);
      this.touchEntitlementFetchedAt();
      await this.refreshKioskAccessCard();
    } catch (e) {
      this.error = e?.message || 'Failed to load account settings';
      this.accountId = '';
      this.settings = null;
      this.controlPlane = emptyControlPlane();
      this.kioskQrDataUrl = '';
      this.kioskQrError = '';
    } finally {
      if (withSpinner) this.loading = false;
    }
    refreshKioskNavEntitlement();
    if (this.activeTab === 'people') await this.loadPeople();
  },

  async loadPeople() {
    const r = String(this.$store?.session?.me?.role || '').toLowerCase();
    if (!['owner', 'admin', 'manager'].includes(r)) return;
    this.peopleLoading = true;
    this.peopleError = '';
    try {
      const [res, pres] = await Promise.all([
        apiFetch('/api/admin/auth/users'),
        apiFetch('/api/admin/players'),
      ]);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || data?.message || 'Could not load users');
      this.peopleItems = (Array.isArray(data.items) ? data.items : []).map((row) => this.normalizePeopleRow(row));
      const pdata = await pres.json().catch(() => ({}));
      this.inviteRosterPlayers = pres.ok && Array.isArray(pdata.items) ? pdata.items : [];
    } catch (e) {
      this.peopleItems = [];
      this.inviteRosterPlayers = [];
      this.peopleError = e?.message || 'Could not load users';
    } finally {
      this.peopleLoading = false;
    }
  },

  async inviteStaff() {
    if (this.inviting || !this.canInviteStaff()) return;
    const email = String(this.inviteForm.email || '').trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.$store.toastr.error('Enter a valid email');
      return;
    }
    const flow = String(this.inviteForm.flow || 'staff').toLowerCase();
    let role = String(this.inviteForm.role || 'manager').toLowerCase();
    let playerId = null;
    if (flow === 'player') {
      role = 'player';
      playerId = String(this.inviteForm.playerId || '').trim();
      if (!playerId) {
        this.$store.toastr.error('Choose the roster player this login is for');
        return;
      }
    } else {
      if (role === 'owner' && String(this.$store?.session?.me?.role || '').toLowerCase() !== 'owner') {
        this.$store.toastr.error('Only the account owner can invite another owner');
        return;
      }
      if (role === 'player') {
        this.$store.toastr.error('Use “Player portal” to invite roster-linked logins');
        return;
      }
    }
    this.inviting = true;
    try {
      const body = { email, role };
      if (playerId) body.playerId = playerId;
      const res = await apiFetch('/api/admin/auth/invite', { method: 'POST', body });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || data?.error || 'Invite failed');
      this.inviteForm = { flow: 'staff', email: '', role: 'manager', playerId: '' };
      const kind = data.inviteKind === 'player' ? 'Player portal' : 'Staff';
      this.$store.toastr.success(`${kind} invite sent to ${email}`);
      await this.loadPeople();
    } catch (e) {
      this.$store.toastr.error(e?.message || 'Invite failed');
    } finally {
      this.inviting = false;
    }
  },

  async resendStaffInvite(row) {
    if (!this.canInviteStaff() || !row?.pendingSetup) return;
    if (String(row.role || '').toLowerCase() === 'player' && !row.playerId) {
      this.$store.toastr.error('Cannot resend: no roster player linked. Send a new player-portal invite.');
      return;
    }
    this.inviting = true;
    try {
      const body = {
        email: String(row.email || '').trim().toLowerCase(),
        role: String(row.role || 'manager').toLowerCase(),
      };
      if (body.role === 'player') body.playerId = String(row.playerId).trim();
      const res = await apiFetch('/api/admin/auth/invite', { method: 'POST', body });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || data?.error || 'Resend failed');
      this.$store.toastr.success('Invite email sent again');
      await this.loadPeople();
    } catch (e) {
      this.$store.toastr.error(e?.message || 'Resend failed');
    } finally {
      this.inviting = false;
    }
  },

  async saveUserRole(row, newRole) {
    if (!this.canManagePeopleRow(row) || this.roleSaveEmail) return;
    const email = String(row?.email || '').trim().toLowerCase();
    const role = String(newRole || '').trim().toLowerCase();
    if (!email || !role) return;
    if (role === String(row?.role || '').toLowerCase()) return;
    if (role === 'owner' && String(this.$store?.session?.me?.role || '').toLowerCase() !== 'owner') {
      this.$store.toastr.error('Only the account owner can assign the owner role');
      await this.loadPeople();
      return;
    }
    this.roleSaveEmail = email;
    try {
      const res = await apiFetch(`/api/admin/auth/users/${encodeURIComponent(email)}/role`, {
        method: 'PUT',
        body: { role },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || data?.error || 'Could not update role');
      row.role = role;
      this.$store.toastr.success('Role updated');
      await this.loadPeople();
    } catch (e) {
      this.$store.toastr.error(e?.message || 'Could not update role');
      await this.loadPeople();
    } finally {
      this.roleSaveEmail = null;
    }
  },

  async removePerson(row) {
    if (!this.canManagePeopleRow(row) || this.removeBusyEmail) return;
    const email = String(row?.email || '').trim().toLowerCase();
    if (!email) return;
    const prompt = row.pendingSetup
      ? `Cancel the invite for ${email}?`
      : `Remove ${email} from this account? They will lose access immediately.`;
    if (!globalThis.confirm(prompt)) return;
    this.removeBusyEmail = email;
    try {
      const res = await apiFetch(`/api/admin/auth/users/${encodeURIComponent(email)}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || data?.error || 'Could not remove user');
      this.$store.toastr.success(row.pendingSetup ? 'Invite cancelled' : 'User removed');
      await this.loadPeople();
    } catch (e) {
      this.$store.toastr.error(e?.message || 'Could not remove user');
    } finally {
      this.removeBusyEmail = null;
    }
  },

  async saveMailDelivery() {
    if (this.savingMail || !this.isVenueOwner()) return;
    this.savingMail = true;
    this.error = '';
    try {
      const body = {
        mode: this.mailForm.mode,
        fromOverride: this.mailForm.fromOverride,
        smtpHost: this.mailForm.smtpHost,
        smtpPort: this.mailForm.smtpPort,
        smtpSecure: this.mailForm.smtpSecure,
        smtpUser: this.mailForm.smtpUser,
        smtpPass: this.mailForm.smtpPass,
      };
      const res = await apiFetch('/api/admin/account-settings/mail-delivery', { method: 'PUT', body });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Could not save mail settings');
      this.mailForm.smtpPass = '';
      this.$store.toastr.success('Mail delivery settings saved');
    } catch (e) {
      this.error = e?.message || 'Could not save mail settings';
      this.$store.toastr.error(this.error);
    } finally {
      this.savingMail = false;
    }
  },

  async sendMailTest() {
    if (this.mailTestLoading || !this.isVenueOwner()) return;
    this.mailTestLoading = true;
    try {
      const to = String(this.mailForm.testTo || '').trim();
      const res = await apiFetch('/api/admin/account-settings/mail-test', { method: 'POST', body: { to } });
      const data = await res.json().catch(() => ({}));
      if (res.status === 429) {
        throw new Error(data?.message || data?.error || 'Too many test sends. Try again later.');
      }
      if (!res.ok || !data.ok) throw new Error(data?.message || data?.error || 'Test send failed');
      this.$store.toastr.success(data.skipped ? 'Mail not configured on server (skipped).' : 'Test email sent');
    } catch (e) {
      this.$store.toastr.error(e?.message || 'Test send failed');
    } finally {
      this.mailTestLoading = false;
    }
  },

  async saveGoogleMapsApiKey() {
    if (this.savingMapsKey || !this.canManageAccountIntegrations()) return;
    const nextKey = String(this.form.googleMapsApiKey || '').trim();
    if (!nextKey) {
      this.$store.toastr.error('Paste a Google Maps API key to save');
      return;
    }
    this.savingMapsKey = true;
    this.error = '';
    try {
      const res = await apiFetch('/api/admin/account-settings', {
        method: 'PUT',
        body: { googleMapsApiKey: nextKey },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Could not save Maps API key');
      this.settings = data.settings || this.settings;
      this.controlPlane = normalizeControlPlane(data.controlPlane || this.controlPlane);
      this.form.googleMapsApiKey = '';
      this.$store.toastr.success('Google Maps API key saved');
    } catch (e) {
      this.error = e?.message || 'Could not save Maps API key';
      this.$store.toastr.error(this.error);
    } finally {
      this.savingMapsKey = false;
    }
  },

  async clearGoogleMapsApiKey() {
    if (this.clearingMapsKey || !this.canManageAccountIntegrations()) return;
    if (!globalThis.confirm('Remove the stored Google Maps API key? Venue maps will stop working until you add a new key.')) return;
    this.clearingMapsKey = true;
    this.error = '';
    try {
      const res = await apiFetch('/api/admin/account-settings', {
        method: 'PUT',
        body: { googleMapsApiKey: '' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Could not clear Maps API key');
      this.settings = data.settings || this.settings;
      this.controlPlane = normalizeControlPlane(data.controlPlane || this.controlPlane);
      this.form.googleMapsApiKey = '';
      this.$store.toastr.success('Google Maps API key removed');
    } catch (e) {
      this.error = e?.message || 'Could not clear Maps API key';
      this.$store.toastr.error(this.error);
    } finally {
      this.clearingMapsKey = false;
    }
  },

  async saveSettings() {
    if (!this.saveControlPlaneUrlAllowed || !this.canManageAccountIntegrations()) return;
    if (this.saving) return;
    this.saving = true;
    this.error = '';
    try {
      const res = await apiFetch('/api/admin/account-settings', {
        method: 'PUT',
        body: {
          controlPlaneBaseUrl: String(this.form.controlPlaneBaseUrl || '').trim(),
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Could not save settings');
      this.settings = data.settings || this.settings;
      this.controlPlane = normalizeControlPlane(data.controlPlane || this.controlPlane);
      applyEffectiveFromAccountResponse(data);
      this.touchEntitlementFetchedAt();
      await this.refreshKioskAccessCard();
      this.$store.toastr.success('Account settings saved');
      refreshKioskNavEntitlement();
    } catch (e) {
      this.error = e?.message || 'Could not save settings';
      this.$store.toastr.error(this.error);
    } finally {
      this.saving = false;
    }
  },

  async saveSubscriptionKey() {
    if (this.savingKey || !this.canManageAccountIntegrations()) return;
    this.savingKey = true;
    this.error = '';
    try {
      const res = await apiFetch('/api/admin/account-settings/subscription-key', {
        method: 'PUT',
        body: { subscriptionKey: String(this.form.subscriptionKey || '') },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Could not activate subscription key');
      this.settings = data.settings || this.settings;
      this.controlPlane = normalizeControlPlane(data.controlPlane || this.controlPlane);
      applyEffectiveFromAccountResponse(data);
      this.form.subscriptionKey = '';
      this.touchEntitlementFetchedAt();
      await this.refreshKioskAccessCard();
      this.$store.toastr.success('Subscription key activated');
      refreshKioskNavEntitlement();
    } catch (e) {
      this.error = e?.message || 'Could not activate subscription key';
      this.$store.toastr.error(this.error);
    } finally {
      this.savingKey = false;
    }
  },

  async clearSubscriptionKey() {
    if (this.clearingKey || !this.canManageAccountIntegrations()) return;
    if (!globalThis.confirm('Clear the stored subscription key for this account?')) return;
    this.clearingKey = true;
    this.error = '';
    try {
      const res = await apiFetch('/api/admin/account-settings/subscription-key', { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Could not clear subscription key');
      this.settings = data.settings || this.settings;
      this.controlPlane = normalizeControlPlane(data.controlPlane || this.controlPlane);
      applyEffectiveFromAccountResponse(data);
      this.form.subscriptionKey = '';
      this.touchEntitlementFetchedAt();
      await this.refreshKioskAccessCard();
      this.$store.toastr.success('Stored subscription key removed');
      refreshKioskNavEntitlement();
    } catch (e) {
      this.error = e?.message || 'Could not clear subscription key';
      this.$store.toastr.error(this.error);
    } finally {
      this.clearingKey = false;
    }
  },

  async refreshEntitlement() {
    if (this.refreshing) return;
    this.refreshing = true;
    this.error = '';
    try {
      const res = await apiFetch('/api/admin/account-settings/refresh', { method: 'POST', body: {}, timeoutMs: 20000 });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Could not refresh entitlement');
      if (data.billingContactEmail !== undefined) {
        this.billingContactEmail = String(data.billingContactEmail || '').trim();
      }
      this.settings = data.settings || this.settings;
      this.controlPlane = normalizeControlPlane(data.controlPlane || this.controlPlane);
      applyEffectiveFromAccountResponse(data);
      this.touchEntitlementFetchedAt();
      await this.refreshKioskAccessCard();
      this.$store.toastr.success('Entitlement refreshed');
      refreshKioskNavEntitlement();
    } catch (e) {
      this.error = e?.message || 'Could not refresh entitlement';
      this.$store.toastr.error(this.error);
    } finally {
      this.refreshing = false;
    }
  },
}));
